import { Plus } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { FieldTableHeader } from "./invoice-item-header";
import { InvoiceItemRow, TInvoiceItemField } from "./invoice-item-row";

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  InvoicingActions,
  InvoicingStatus,
} from "@/constants/invoicing.constants";
import { useInvoicingContext } from "@/pages/invoicing/context/InvoicingContext";
import { useDocketInvoicing } from "@/pages/invoicing/docket-invoice/_context/DocketInvoiceSheetContext";
import * as invoicingSchema from "@/schemas/invoicing.schema";

const ViewInvoiceSheetContent = (): JSX.Element => {
  const { trackingNameOptions, xeroTrackingItems, isInvoicingForDocket } =
    useInvoicingContext();
  const { formattedInvoicingDetails, setFormattedInvoicingDetails } =
    useDocketInvoicing();

  const form = useFormContext<invoicingSchema.CreateDocketInvoicing>();
  const { control, setValue, getValues, watch } = form;

  const { status, action } = getValues();

  const isDisableFormField =
    action === InvoicingActions.VIEW && status !== InvoicingStatus.DRAFT;

  const { fields, remove, append } = useFieldArray({
    control,
    name: "itemSheets",
    keyName: "idx",
  });

  const commonSelectClassName = useMemo(() => " w-[250px] mt-2", []);
  const commonInputClassName = useMemo(() => "w-[150px] mt-2", []);
  const commonCellClassName = useMemo(
    () => "align-top bg-white hover:bg-white",
    [],
  );

  const emptyInvoicingItem = useMemo(
    () => invoicingSchema.emptyInvoicingItems,
    [],
  );

  const handleAddRow = useCallback(() => {
    append(emptyInvoicingItem);
  }, [append, emptyInvoicingItem]);

  const handleRemoveRow = useCallback(
    (index: number, field: TInvoiceItemField) => {
      remove(index);
      if (
        getValues("action") === InvoicingActions.VIEW &&
        getValues("status") === InvoicingStatus.DRAFT &&
        isInvoicingForDocket &&
        field?.isDraftDocket
      ) {
        const selectedDocketIds = watch("selectedDocketIds");
        //Check if the selected docket is in the selectedDocketIds
        const isDocketSelected = selectedDocketIds?.includes(
          field?.docketId ?? "",
        );
        if (isDocketSelected) {
          // Remove the selected docket from the selectedDocketIds
          const updatedSelectedDocketIds = selectedDocketIds?.filter(
            (id) => id !== field?.docketId,
          );
          setValue("selectedDocketIds", updatedSelectedDocketIds, {
            shouldDirty: true,
          });
          // Remove the selected docket from the formattedInvoicingDetails by comparing the index
          const updatedFormattedInvoicingDetails =
            formattedInvoicingDetails?.filter((_, i) => i !== index);
          setFormattedInvoicingDetails(updatedFormattedInvoicingDetails);
        }
      }
    },
    [
      remove,
      getValues,
      isInvoicingForDocket,
      watch,
      setValue,
      formattedInvoicingDetails,
      setFormattedInvoicingDetails,
    ],
  );

  return (
    <div className='overflow-auto border rounded-md'>
      <Table>
        <FieldTableHeader />
        <TableBody>
          {fields?.map((field, index) => (
            <InvoiceItemRow
              key={field?.idx}
              field={field}
              index={index}
              commonCellClassName={commonCellClassName}
              commonSelectClassName={commonSelectClassName}
              commonInputClassName={commonInputClassName}
              isDisableFormField={isDisableFormField}
              trackingNameOptions={trackingNameOptions}
              xeroTrackingItems={xeroTrackingItems}
              onRemoveRow={handleRemoveRow}
            />
          ))}
          {getValues("action") !== InvoicingActions.VIEW && (
            <TableRow className='!bg-white dark:bg-primary'>
              <TableCell colSpan={11} className='text-left'>
                <Button
                  type='button'
                  variant={"ghost"}
                  className='border border-dashed border-input'
                  onClick={handleAddRow}
                  aria-label='Add new item'
                >
                  <Plus size={20} className='mr-2 ' />
                  New Item
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default React.memo(ViewInvoiceSheetContent);
